import React, { useEffect } from 'react';
import { Navigate } from 'react-router';
import useAppSettings from '../../hooks/useAppSettings';
import LoadingPage from '../LoadingPage';
import LandingPage from './LandingPage';
import { LandingPageWrapperProps } from './types';
import QueryErrorHandler from '../../components/ErrorBoundary/QueryErrorHandler';

// Global flags to track autoLoad state (persist across component unmounts)
// Exported so other components can mark the app as "used"
export let hasAutoLoadBeenChecked = false;
export const markAutoLoadAsChecked = () => {
  hasAutoLoadBeenChecked = true;
};
export let isAutoLoadRedirecting = false;
export const markAutoLoadRedirecting = () => {
  isAutoLoadRedirecting = false;
};

function LandingPageWrapper({ loadDataset, openDataLoader }: LandingPageWrapperProps) {
  const appSettingsQuery = useAppSettings();
  const isFirstLandingPageVisit = !hasAutoLoadBeenChecked;

  // Reset redirecting flag when component unmounts (navigation completed)
  useEffect(() => {
    return () => {
      if (isAutoLoadRedirecting) {
        isAutoLoadRedirecting = false;
      }
    };
  }, []);

  // Wait for settings to load
  if (appSettingsQuery.isLoading) {
    return <LoadingPage />;
  }

  if (appSettingsQuery.isError) {
    return <QueryErrorHandler appSettingsQuery={appSettingsQuery} />;
  }

  const { startupMode, lastLoadedDatasetId } = appSettingsQuery.data;

  // Only autoLoad if this is the first landing page visit (app loaded at /)
  if (isFirstLandingPageVisit && startupMode) {
    hasAutoLoadBeenChecked = true;
    isAutoLoadRedirecting = true;

    if (startupMode === 'example') {
      return <Navigate to="/example" replace />;
    }
    if (startupMode === 'last-loaded' && lastLoadedDatasetId) {
      return <Navigate to={`/${lastLoadedDatasetId}`} replace />;
    }

    isAutoLoadRedirecting = false;
  }

  // If we're in the middle of an autoLoad redirect, keep showing loading
  if (isAutoLoadRedirecting) {
    return <LoadingPage />;
  }

  // Show landing page (either no autoLoad or returning home)
  return <LandingPage loadDataset={loadDataset} openDataLoader={openDataLoader} />;
}

export default LandingPageWrapper;
